import { useState } from 'react';
import { X } from 'lucide-react';

// ──────────────────────────────────────────────────────────────────────
// Iter 63 Item 1 — Multi-assignee chip-input picker.
//
// Reusable across Quick Create (UnifiedTasksPage), client-scoped New
// Task (MyClientsPage), and any other surface that needs to pick 1-N
// users from a workspace member list.
//
// Props
//   members      Array<{id, name, email, role?}> — workspace members
//   selected     string[] — currently picked user IDs
//   onChange     (newIds: string[]) => void
//   placeholder  optional placeholder text when empty
//   showRole     when true (Iter 63 Item 2), suffix " (<Role>)" on the
//                option label and chip
//   testIdPrefix optional override for data-testids
// ──────────────────────────────────────────────────────────────────────
function roleLabel(role) {
  if (!role) return null;
  const r = String(role).toLowerCase();
  if (r === 'owner' || r === 'super_admin') return 'Owner';
  if (r === 'admin') return 'Admin';
  if (r === 'manager') return 'Manager';
  if (r === 'member' || r === 'team') return 'Member';
  // capitalize unknown
  return r.charAt(0).toUpperCase() + r.slice(1);
}

function nameWithRole(m, showRole) {
  const display = m?.name || m?.email || '?';
  if (!showRole) return display;
  const r = roleLabel(m?.role);
  return r ? `${display} (${r})` : display;
}

export function AssigneeMultiSelect({
  members = [],
  selected = [],
  onChange,
  placeholder = 'Click to add assignees',
  showRole = false,
  testIdPrefix = 'assignee',
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const filtered = (members || []).filter(m =>
    !selected.includes(m.id) &&
    (!q.trim() || (m.name || '').toLowerCase().includes(q.toLowerCase())
      || (m.email || '').toLowerCase().includes(q.toLowerCase()))
  );
  const remove = (id) => onChange(selected.filter(x => x !== id));
  const add = (id) => { onChange([...selected, id]); setQ(''); };
  return (
    <div className="relative">
      <div
        className="min-h-[40px] rounded-lg bg-foreground/[0.02] border border-border/30 px-2 py-1 flex flex-wrap items-center gap-1 cursor-text"
        onClick={() => setOpen(true)}
        data-testid={`${testIdPrefix}-input`}
      >
        {selected.length === 0 && (
          <span className="text-[12px] text-muted-foreground/40 px-1">{placeholder}</span>
        )}
        {selected.map(id => {
          const m = (members || []).find(x => x.id === id);
          return (
            <span
              key={id}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gold/10 text-gold text-[11px]"
              data-testid={`${testIdPrefix}-chip-${id}`}
            >
              {nameWithRole(m, showRole) || (id || '').slice(0, 8)}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); remove(id); }}
                className="hover:opacity-70"
                data-testid={`${testIdPrefix}-chip-remove-${id}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          );
        })}
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={selected.length === 0 ? '' : 'Add more…'}
          className="bg-transparent outline-none text-[12px] flex-1 min-w-[80px] px-1"
          data-testid={`${testIdPrefix}-search`}
        />
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border/40 rounded-lg shadow-xl z-[100] max-h-48 overflow-y-auto">
          {filtered.slice(0, 8).map(m => (
            <button
              key={m.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); add(m.id); }}
              className="flex items-center gap-2 w-full px-3 py-2 text-left text-[12px] hover:bg-foreground/[0.04]"
              data-testid={`${testIdPrefix}-option-${m.id}`}
            >
              <div className="h-5 w-5 rounded-full bg-secondary flex items-center justify-center shrink-0">
                <span className="text-[8px] font-medium">{(m.name || m.email || '?')[0].toUpperCase()}</span>
              </div>
              <span className="truncate flex-1">{nameWithRole(m, showRole)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export { roleLabel, nameWithRole };
